// Environment Manager — Fase 15.1
export type AppEnvironment = 'development' | 'staging' | 'production'

export interface EnvVar {
  key: string; required: boolean; description: string; sensitive: boolean; defaultValue?: string
}
export interface EnvValidationResult {
  environment: AppEnvironment; valid: boolean; missing: string[]; warnings: string[]
  loaded: Record<string, string | undefined>; timestamp: string
}

const REQUIRED_VARS: EnvVar[] = [
  { key: 'VITE_SUPABASE_URL',       required: true,  description: 'Supabase project URL',          sensitive: false },
  { key: 'VITE_SUPABASE_ANON_KEY',  required: true,  description: 'Supabase anon key',             sensitive: true  },
  { key: 'VITE_APP_VERSION',        required: false, description: 'App semantic version',           sensitive: false, defaultValue: '0.0.0' },
  { key: 'VITE_ENV',                required: false, description: 'Environment override',           sensitive: false, defaultValue: 'development' },
  { key: 'VITE_SENTRY_DSN',         required: false, description: 'Sentry DSN for error tracking',  sensitive: true  },
  { key: 'VITE_DATADOG_RUM_APP_ID', required: false, description: 'DataDog RUM app ID',            sensitive: true  },
]

function detectEnvironment(): AppEnvironment {
  const envVar = import.meta.env['VITE_ENV'] as string | undefined
  if (envVar === 'production') return 'production'
  if (envVar === 'staging')    return 'staging'
  if (import.meta.env.PROD)    return 'production'
  return 'development'
}

export function getEnvironment(): AppEnvironment  { return detectEnvironment() }
export function isProduction(): boolean            { return getEnvironment() === 'production' }
export function isStaging(): boolean               { return getEnvironment() === 'staging' }
export function isDevelopment(): boolean           { return getEnvironment() === 'development' }

export function safeEnvGet(key: string): string | undefined {
  return import.meta.env[key] as string | undefined
}
export function safeEnvRequire(key: string): string {
  const v = safeEnvGet(key)
  if (!v) throw new Error(`[env] Required env var missing: ${key}`)
  return v
}

export function validateEnvironment(): EnvValidationResult {
  const environment = detectEnvironment()
  const missing: string[] = []; const warnings: string[] = []
  const loaded: Record<string, string | undefined> = {}
  for (const v of REQUIRED_VARS) {
    const val = safeEnvGet(v.key) ?? v.defaultValue
    loaded[v.key] = v.sensitive ? (val ? '[REDACTED]' : undefined) : val
    if (!val) { if (v.required) missing.push(v.key); else warnings.push(`Optional not set: ${v.key}`) }
  }
  if (environment === 'production' && !safeEnvGet('VITE_SENTRY_DSN'))
    warnings.push('Production without VITE_SENTRY_DSN — errors untracked externally')
  return { environment, valid: missing.length === 0, missing, warnings, loaded, timestamp: new Date().toISOString() }
}

export function printEnvReport(r: EnvValidationResult): void {
  console.group(`[env] ${r.environment} — ${r.valid ? 'valid' : 'INVALID'}`)
  if (r.missing.length)  console.error('[env] Missing:', r.missing)
  if (r.warnings.length) r.warnings.forEach(w => console.warn('[env]', w))
  console.groupEnd()
}
